// pages/ProjectInformationPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, FormControl, Select, MenuItem, ListSubheader, Checkbox, ListItemText,
  Button, Pagination, LinearProgress, Chip, IconButton, Tooltip,
  Autocomplete, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import GridViewOutlined from "@mui/icons-material/GridViewOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";

// Firestore
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";

/* ================= helpers ================= */
const norm = (s) => (s ?? "").toString().trim().toLowerCase();
// collapse spaces + punctuation for robust name matching (e.g., "K S" == "KS")
const idNorm = (s) => norm(s).replace(/[^a-z0-9]/g, "");

const getProjectId = (p) => p?.id ?? p?.docId ?? p?.projectId ?? "";
const getProjectName = (p) => p?.name ?? p?.projectName ?? p?.title ?? "";
const getProjectEntity = (p) => p?.entity ?? p?.entityName ?? "";

const getTaskProjectId = (t) => t?.projectId ?? t?.project_id ?? "";
const getTaskProjectName = (t) => t?.project ?? t?.projectName ?? "";
const getTaskEntity = (t) => t?.entity ?? t?.entityName ?? "";

// task ↔ project linking
const taskBelongsToProject = (t, p) => {
  const tPid = getTaskProjectId(t);
  const pPid = getProjectId(p);
  if (tPid && pPid) return tPid === pPid;
  // punctuation-agnostic fallback by name + entity
  return (
    idNorm(getTaskProjectName(t)) === idNorm(getProjectName(p)) &&
    idNorm(getTaskEntity(t)) === idNorm(getProjectEntity(p))
  );
};

const normStatus = (s) => {
  const n = norm(s);

  // ✅ ADD THIS BLOCK
  if (n === "not applicable" || n === "na" || n === "n/a") return "not-applicable";

  if (n === "completed" || n === "done") return "completed";
  if (["in progress", "in-progress", "progress", "ongoing"].includes(n)) return "in-progress";
  if (["pending", "to do", "todo", "not started"].includes(n)) return "pending";
  return n || "pending";
};

const toJSDate = (v) => (v?.toDate ? v.toDate() : new Date(v));
const formatDate = (v) => {
  if (!v) return "—";
  const d = toJSDate(v);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};
const deadlineText = (due) => {
  if (!due) return "—";
  const d = toJSDate(due);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const days = Math.round((d - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return "Overdue";
  if (days < 31) return `${days} Day${days === 1 ? "" : "s"}`;
  const months = Math.round(days / 30);
  return `${months} Month${months === 1 ? "" : "s"}`;
};

// Parse dd-mm-yyyy or dd/mm/yyyy → Date
const parseDMY = (str) => {
  if (!str) return null;
  const [dd, mm, yyyy] = String(str).split(/[-/]/);
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(d.getTime()) ? null : d;
};

// Signed days from today for a DMY string
const daysLeftFromDMY = (ddmmyyyy) => {
  const d = parseDMY(ddmmyyyy);
  if (!d) return null;
  return Math.ceil((d - new Date()) / 86400000);
};

// QS helpers: encode/decode array params (existing page behavior)
const toQS = (arr) => (arr && arr.length ? arr.map(encodeURIComponent).join(",") : "");
const fromQS = (s) => (s ? String(s).split(",").map(decodeURIComponent).filter(Boolean) : []);

const chipColorForStageDMY = (stage, dueDMY) => {
  const s = (stage || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "in progress") return "warning";
  const left = daysLeftFromDMY(dueDMY);
  return left !== null && left < 0 ? "error" : "default";
};

// Category choices (same taxonomy you use elsewhere)
const CATEGORY_OPTIONS = [
  "Leadership (owners, managing partners)",
  "Engagement Heads (Experienced Professionals)",
  "Qualified Professionals (CA, CS, ACCA or Other Post Graduate qualifications)",
  "Semi-Qualified Staff (pursuing professional course)",
  "Trainee’s (Interns etc)",
  "Support Staff (other employees)",
  "HR (internal administrative staff)",
];

/* ================= component ================= */
export default function ProjectInformationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bootedQS, setBootedQS] = useState(false);

  const { companyId, user } = useAuth();
  const [loading, setLoading] = useState(false);

  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Same normalization used everywhere
  const meUid = user?.uid || "";
  const meNameN = idNorm(user?.name || "");
  const meRole = user?.role || "Member";
  const isAdminOrManager = meRole === "Admin" || meRole === "Manager";

  /* ================= Reviewed toggle filter ================= */
  // "All" | "Reviewed" | "Not Reviewed"
  const [reviewedFilter, setReviewedFilter] = useState("All");

  const applyReviewedFilter = (arr) => {
    if (reviewedFilter === "Reviewed") return arr.filter((t) => Boolean(t?.reviewed));
    if (reviewedFilter === "Not Reviewed") return arr.filter((t) => !Boolean(t?.reviewed));
    return arr;
  };

  /* -------- Fetch projects & tasks (same as before) -------- */
  useEffect(() => {
    const run = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        // Projects
        const pSnap = await getDocs(query(collection(db, "projects"), where("companyId", "==", companyId)));
        const p = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProjects(p);

        // Tasks (role-aware)
        if (meRole === "Admin") {
          const tSnap = await getDocs(query(collection(db, "tasks"), where("companyId", "==", companyId)));
          setTasks(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else if (meRole === "Manager") {
          // Manager → only tasks under projects they manage
          const managedNames = new Set(
            p.filter((pr) => pr?.managerId === meUid || idNorm(pr?.managerName) === meNameN)
              .map((pr) => (getProjectName(pr) || "").trim())
          );
          const tSnap = await getDocs(query(collection(db, "tasks"), where("companyId", "==", companyId)));
          const all = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setTasks(all.filter((t) => managedNames.has((getTaskProjectName(t) || "").trim())));
        } else {
          // Member → fetch all; we’ll scope later
          const tSnap = await getDocs(query(collection(db, "tasks"), where("companyId", "==", companyId)));
          setTasks(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [companyId, meRole, meUid, meNameN]);

  /* -------- Company users (for filters) -------- */
  const [companyUsers, setCompanyUsers] = useState([]); // {id,name,isActive,category}
  useEffect(() => {
    if (!companyId || !isAdminOrManager) return;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "users"), where("companyId", "==", companyId)));
        const arr = snap.docs.map((d) => {
          const u = d.data() || {};
          const name = u.name || u.displayName || u.email || "Unnamed";
          return {
            id: d.id,
            name,
            isActive: (u.isActive ?? true) === true,
            category: u.category || "",
          };
        });
        arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCompanyUsers(arr);
      } catch (e) {
        console.error("users list failed:", e);
        setCompanyUsers([]);
      }
    })();
  }, [companyId, isAdminOrManager]);

  // name(idNorm) -> userMeta (isActive/category)
  const userMetaByName = useMemo(() => {
    const m = new Map();
    companyUsers.forEach((u) => m.set(idNorm(u.name), u));
    return m;
  }, [companyUsers]);

  // All assignee names from the *visible* tasks (used to constrain the user filter for Managers)
  const usersFromVisible = useMemo(() => {
    const s = new Set((tasks || []).map((t) => (t.assignedTo || "").trim()).filter(Boolean));
    return s;
  }, [tasks]);

  // Options for "Filter by user"
  const usersForFilter = useMemo(() => {
    if (meRole === "Admin") return companyUsers;
    // Manager → only names appearing in their visible tasks
    const allow = new Set([...usersFromVisible].map((n) => n.toLowerCase()));
    const matched = (companyUsers || []).filter((u) =>
      allow.has((u?.name || "").trim().toLowerCase())
    );
    // add fallback for names not in users collection
    const missing = [...usersFromVisible]
      .filter((n) => !matched.some((u) => (u?.name || "").trim().toLowerCase() === n.toLowerCase()))
      .map((n) => ({ id: `name:${n}`, name: n }));
    return [...matched, ...missing].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [meRole, companyUsers, usersFromVisible]);

  /* -------- Global meta filters (Admin/Manager only) -------- */
  const [selectedUser, setSelectedUser] = useState(null); // object from usersForFilter
  const [filterStatus, setFilterStatus] = useState(""); // "", "Active", "Inactive"
  const [filterCategories, setFilterCategories] = useState([]); // array of strings

  /** Make User/Category options aware of each other (and Status) */

  // 1) Users list constrained by Status + Category
  const usersForFilterScoped = useMemo(() => {
    let arr = usersForFilter;

    // by status
    if (filterStatus) {
      const wantActive = filterStatus === "Active";
      arr = arr.filter((u) => (u.isActive ?? true) === wantActive);
    }

    // by category
    if (filterCategories.length) {
      const set = new Set(filterCategories);
      arr = arr.filter((u) => set.has(u.category || ""));
    }

    // sort by name for stable UX
    return [...arr].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [usersForFilter, filterStatus, filterCategories]);

  // 2) Category options constrained by Status + the currently valid Users list
  const categoryOptionsScoped = useMemo(() => {
    const set = new Set(
      usersForFilterScoped.map((u) => u.category || "").filter(Boolean)
    );
    return CATEGORY_OPTIONS.filter((c) => set.has(c));
  }, [usersForFilterScoped]);

  // 3) Keep selections valid when options shrink
  useEffect(() => {
    if (selectedUser && !usersForFilterScoped.some((u) => u.id === selectedUser.id)) {
      setSelectedUser(null);
    }
  }, [usersForFilterScoped, selectedUser]);

  useEffect(() => {
    if (filterCategories.length === 0) return;
    const allowed = new Set(categoryOptionsScoped);
    const next = filterCategories.filter((c) => allowed.has(c));
    if (next.length !== filterCategories.length) setFilterCategories(next);
  }, [categoryOptionsScoped, filterCategories]);

  /* -------- Who am I Team Lead for? -------- */
  const leadProjectIds = useMemo(() => {
    if (!projects?.length) return new Set();
    return new Set(
      projects
        .filter((p) => {
          const tlId = p?.teamLeadId || p?.teamLeaderId;
          const tlNameN = idNorm(p?.teamLeadName || p?.teamLeaderName || "");
          return (tlId && tlId === meUid) || (!!tlNameN && tlNameN === meNameN);
        })
        .map((p) => getProjectId(p))
        .filter(Boolean)
    );
  }, [projects, meUid, meNameN]);

  const leadProjectNames = useMemo(() => {
    if (!projects?.length) return new Set();
    return new Set(
      projects
        .filter((p) => {
          const tlId = p?.teamLeadId || p?.teamLeaderId;
          const tlNameN = idNorm(p?.teamLeadName || p?.teamLeaderName || "");
          return (tlId && tlId === meUid) || (!!tlNameN && tlNameN === meNameN);
        })
        .map((p) => (getProjectName(p) || "").trim())
        .filter(Boolean)
    );
  }, [projects, meUid, meNameN]);

  const iAmTLForProject = (p) => {
    const id = getProjectId(p);
    const name = (getProjectName(p) || "").trim();
    return (id && leadProjectIds.has(id)) || leadProjectNames.has(name);
  };

  /* -------- Visible projects by role -------- */
  // Projects where I personally have tasks
  const myAssignedProjectNames = useMemo(() => {
    const mine = tasks.filter((t) => idNorm(t?.assignedTo) === meNameN);
    return new Set(mine.map((t) => (getTaskProjectName(t) || "").trim()));
  }, [tasks, meNameN]);

  const visibleProjects = useMemo(() => {
    if (!projects?.length) return [];
    if (meRole === "Admin") return projects;
    if (meRole === "Manager") {
      return projects.filter((p) => p?.managerId === meUid || idNorm(p?.managerName) === meNameN);
    }
    // Member → TL projects + projects where I have tasks
    return projects.filter(
      (p) => iAmTLForProject(p) || myAssignedProjectNames.has((getProjectName(p) || "").trim())
    );
  }, [projects, meRole, meUid, meNameN, myAssignedProjectNames]);

  // Apply selectedUser + Category + Active/Inactive against an array of tasks
  const applyUserMetaFilters = (arr) => {
    if (!isAdminOrManager) return arr;

    let out = arr;

    // selected user
    if (selectedUser) {
      const sel = idNorm(selectedUser.name);
      out = out.filter((t) => idNorm(t.assignedTo) === sel);
    }

    // category / status
    if (filterCategories.length || filterStatus) {
      out = out.filter((t) => {
        const meta = userMetaByName.get(idNorm(t.assignedTo));
        if (!meta) return false;
        const okCat = filterCategories.length ? filterCategories.includes(meta.category || "") : true;
        const okStatus = filterStatus ? (filterStatus === "Active" ? meta.isActive : !meta.isActive) : true;
        return okCat && okStatus;
      });
    }

    return out;
  };

  /* -------- Which tasks count for a project row? (filter-aware) -------- */
  const tasksForProject = (p) => {
    // base set for this project
    let allForProject = tasks.filter((t) => taskBelongsToProject(t, p));

    if (meRole === "Admin") {
      allForProject = applyUserMetaFilters(allForProject);
      allForProject = applyReviewedFilter(allForProject);
      return allForProject;
    }

    if (meRole === "Manager") {
      allForProject = applyUserMetaFilters(allForProject);
      allForProject = applyReviewedFilter(allForProject);
      return allForProject;
    }

    // Member:
    // If I'm TL on this project → ALL tasks under that project
    if (iAmTLForProject(p)) {
      return applyReviewedFilter(allForProject);
    }

    // otherwise just my own tasks on that project
    const mine = allForProject.filter((t) => idNorm(t?.assignedTo) === meNameN);
    return applyReviewedFilter(mine);
  };

  /* ================= table / filters / sort ================= */
  const [projPageSize, setProjPageSize] = useState(10);
  const [projPage, setProjPage] = useState(1);

  const [entitySel, setEntitySel] = useState([]);
  const [stageSel, setStageSel] = useState([]);
  const [projectSel, setProjectSel] = useState([]);
  const [categorySel, setCategorySel] = useState([]);

  const [entityQuery, setEntityQuery] = useState("");
  const [stageQuery, setStageQuery] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [categoryQuery, setCategoryQuery] = useState("");

  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");

  const clearFilters = () => {
    setEntitySel([]); setProjectSel([]); setStageSel([]); setCategorySel([]);
    setEntityQuery(""); setProjectQuery(""); setStageQuery(""); setCategoryQuery("");
    setDueFrom(""); setDueTo(""); setProjPage(1);

    // ✅ Reset Reviewed toggle only (do NOT reset admin/manager meta filters)
    setReviewedFilter("All");

    // keep existing behavior
    setSearchParams({}, { replace: true });
  };

  const [sortField, setSortField] = useState("stage");
  const [sortOrder, setSortOrder] = useState("desc");
  const handleSort = (field) => {
    setSortOrder((prev) => (sortField === field ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortField(field);
  };
  const arrow = (field) => (sortField === field ? (sortOrder === "asc" ? " ↑" : " ↓") : "");

  // Are any of the global meta-filters active for Admin/Manager?
  const hasMetaUserFilters = useMemo(
    () => isAdminOrManager && (!!selectedUser || !!filterStatus || filterCategories.length > 0),
    [isAdminOrManager, selectedUser, filterStatus, filterCategories]
  );

  /* -------- rows -------- */
  const rows = useMemo(() => {
    const mapped = visibleProjects.map((p) => {
      const arr = tasksForProject(p); // role-aware + meta-filter-aware + reviewed-filter-aware
      const total = arr.length;

      let comp = 0, prog = 0, pend = 0, na = 0;
      for (const t of arr) {
        const s = normStatus(t.status);
        if (s === "completed") comp++;
        else if (s === "not-applicable") na++;
        else if (s === "in-progress") prog++;
        else pend++;
      }

      // reviewed count (treat undefined as false)
      const reviewedCount = arr.reduce((acc, t) => acc + (Boolean(t?.reviewed) ? 1 : 0), 0);

      const done = comp + na;
      const completion = total ? Math.round((done / total) * 100) : 0;

      const due = p?.dueDate || null;
      return {
        entity: getProjectEntity(p) || "—",
        title: getProjectName(p) || "—",
        category: p?.category || "—",
        dueDate: formatDate(due),
        deadline: deadlineText(due),

        totalTasks: total, // ✅ unchanged (reviewed is NOT added)
        pendingCount: pend,
        inProgressCount: prog,
        completedCount: comp,

        // ✅ NEW: NA count (display column only)
        naCount: na,

        reviewedCount,
        completion,

        stage:
          total === 0
            ? p?.status || p?.priority || "—"
            : done === total
              ? "Completed"
              : prog > 0
                ? "In Progress"
                : "Pending",
      };
    });

    /**
     * ✅ IMPORTANT FIX:
     * When Reviewed toggle is active (Reviewed / Not Reviewed), users expect only matching projects,
     * not rows with 0 tasks (0 reviewed). So hide rows where totalTasks === 0 whenever:
     * - meta filters are active (existing behavior), OR
     * - reviewedFilter is not "All" (NEW behavior)
     */
    const hideZeroTaskRows = hasMetaUserFilters || reviewedFilter !== "All";
    return hideZeroTaskRows ? mapped.filter((r) => r.totalTasks > 0) : mapped;
  }, [
    visibleProjects,
    tasks, // used indirectly by tasksForProject
    isAdminOrManager,
    selectedUser,
    filterStatus,
    filterCategories,
    userMetaByName,
    reviewedFilter, // ✅ ensure recompute on toggle
  ]);

  /* -------- entity / project / stage options (existing logic) -------- */
  const entityOptions = useMemo(() => {
    const s = new Set(rows.map((r) => r.entity).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const categoryOptions = useMemo(() => {
    let base = rows;
    if (entitySel.length) base = base.filter((r) => entitySel.includes(r.entity));
    if (projectSel.length) base = base.filter((r) => projectSel.includes(r.title));
    const s = new Set(base.map((r) => r.category).filter((c) => c && c !== "—"));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows, entitySel, projectSel]);

  const projectOptions = useMemo(() => {
    const base = entitySel.length ? rows.filter((r) => entitySel.includes(r.entity)) : rows;
    const s = new Set(base.map((r) => r.title).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows, entitySel]);

  useEffect(() => {
    if (!projectSel.length) return;
    const allowed = new Set(projectOptions);
    const next = projectSel.filter((p) => allowed.has(p));
    if (next.length !== projectSel.length) setProjectSel(next);
  }, [projectOptions, projectSel]);

  useEffect(() => {
    if (!categorySel.length) return;
    const allowed = new Set(categoryOptions);
    const next = categorySel.filter((v) => allowed.has(v));
    if (next.length !== categorySel.length) setCategorySel(next);
  }, [categoryOptions, categorySel]);

  const stageOptions = useMemo(() => {
    let base = rows;
    if (entitySel.length) base = base.filter((r) => entitySel.includes(r.entity));
    if (projectSel.length) base = base.filter((r) => projectSel.includes(r.title));
    const s = new Set(base.map((r) => r.stage).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows, entitySel, projectSel]);

  useEffect(() => {
    if (!stageSel.length) return;
    const allowed = new Set(stageOptions);
    const next = stageSel.filter((v) => allowed.has(v));
    if (next.length !== stageSel.length) setStageSel(next);
  }, [stageOptions, stageSel]);

  // Boot existing URL filters once
  useEffect(() => {
    if (bootedQS) return;

    const ent = fromQS(searchParams.get("entity"));
    const proj = fromQS(searchParams.get("project"));
    const cat = fromQS(searchParams.get("category"));
    const stg = fromQS(searchParams.get("stage"));
    const df = searchParams.get("from") || "";
    const dt = searchParams.get("to") || "";
    const sf = searchParams.get("sort") || "";
    const so = searchParams.get("order") || "";
    const ps = Number(searchParams.get("ps") || "") || null;
    const p = Number(searchParams.get("p") || "") || null;

    // boot Reviewed toggle from QS
    const rev = (searchParams.get("rev") || "").toLowerCase();
    if (rev === "reviewed") setReviewedFilter("Reviewed");
    else if (rev === "notreviewed") setReviewedFilter("Not Reviewed");
    else setReviewedFilter("All");

    if (ent.length) setEntitySel(ent);
    if (proj.length) setProjectSel(proj);
    if (cat.length) setCategorySel(cat);
    if (stg.length) setStageSel(stg);
    if (df) setDueFrom(df);
    if (dt) setDueTo(dt);
    if (sf) setSortField(sf);
    if (so === "asc" || so === "desc") setSortOrder(so);
    if (ps && [5, 10, 20, 50, 100].includes(ps)) setProjPageSize(ps);
    if (p && p > 0) setProjPage(p);

    setBootedQS(true);
  }, [bootedQS, searchParams]);

  // Persist existing filters/sort/pagination to URL
  useEffect(() => {
    if (!bootedQS) return;
    const next = {};
    if (entitySel.length) next.entity = toQS(entitySel);
    if (projectSel.length) next.project = toQS(projectSel);
    if (categorySel.length) next.category = toQS(categorySel);
    if (stageSel.length) next.stage = toQS(stageSel);
    if (dueFrom) next.from = dueFrom;
    if (dueTo) next.to = dueTo;
    if (sortField) next.sort = sortField;
    if (sortOrder) next.order = sortOrder;
    if (projPageSize !== 10) next.ps = String(projPageSize);
    if (projPage !== 1) next.p = String(projPage);

    // persist Reviewed toggle to QS
    if (reviewedFilter === "Reviewed") next.rev = "reviewed";
    else if (reviewedFilter === "Not Reviewed") next.rev = "notReviewed";
    // if All → omit param to keep URLs clean

    setSearchParams(next, { replace: true });
  }, [
    bootedQS,
    entitySel, projectSel, categorySel, stageSel,
    dueFrom, dueTo,
    sortField, sortOrder,
    projPage, projPageSize,
    reviewedFilter,
    setSearchParams,
  ]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const entOk = entitySel.length === 0 || entitySel.includes(r.entity);
      const projOk = projectSel.length === 0 || projectSel.includes(r.title);
      const catOk = categorySel.length === 0 || categorySel.includes(r.category);
      const stageOk = stageSel.length === 0 || stageSel.includes(r.stage);

      const d = parseDMY(r.dueDate);
      const fromOk = !dueFrom || (d && d >= new Date(dueFrom));
      const toOk = !dueTo || (d && d <= new Date(`${dueTo}T23:59:59`));

      return entOk && projOk && catOk && stageOk && fromOk && toOk;
    });
  }, [rows, entitySel, projectSel, stageSel, dueFrom, dueTo, categorySel]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    const toNum = (v) => (typeof v === "number" ? v : Number(v) || 0);
    const toStr = (v) => (v ?? "").toString().toLowerCase();
    const getDateMs = (r) => {
      const d = parseDMY(r.dueDate);
      return d ? d.getTime() : 0;
    };
    const getVal = (r) => {
      switch (sortField) {
        case "entity": return toStr(r.entity);
        case "title": return toStr(r.title);
        case "dueDate":
        case "deadline": return getDateMs(r);
        case "totalTasks": return toNum(r.totalTasks);
        case "pendingCount": return toNum(r.pendingCount);
        case "inProgressCount": return toNum(r.inProgressCount);
        case "completedCount": return toNum(r.completedCount);

        // ✅ NEW: NA sorting support
        case "naCount": return toNum(r.naCount);

        case "reviewedCount": return toNum(r.reviewedCount);
        case "completion": return toNum(r.completion);
        case "stage": return toStr(r.stage);
        default: return toStr(r.title);
      }
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      const A = getVal(a);
      const B = getVal(b);
      if (A < B) return sortOrder === "asc" ? -1 : 1;
      if (A > B) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortField, sortOrder]);

  // pagination
  const fromIdx = (projPage - 1) * projPageSize;
  const toIdx = Math.min(sorted.length, fromIdx + projPageSize);
  const pageRows = sorted.slice(fromIdx, toIdx);

  const goToSummary = (row) => {
    const params = new URLSearchParams();
    const entity = (row.entity || "").trim();
    const project = (row.title || "").trim();
    if (row.projectId) params.set("projectId", row.projectId);
    if (entity) params.set("entity", entity);
    if (project) params.set("project", project);
    navigate(`/user-task-summary?${params.toString()}`);
  };

  /* ================= render ================= */
  return (
    <Box p={3}>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Project Information</Typography>
        </Box>

        {/* Global meta filters (Admin/Manager only) */}
        {isAdminOrManager && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
            {/* User */}
            <Autocomplete
              size="small"
              sx={{ width: 260 }}
              options={usersForFilterScoped}
              value={selectedUser}
              onChange={(_, v) => setSelectedUser(v)}
              getOptionLabel={(o) => o?.name ?? ""}
              isOptionEqualToValue={(opt, val) =>
                opt?.id && val?.id
                  ? opt.id === val.id
                  : (opt?.name || "").trim().toLowerCase() === (val?.name || "").trim().toLowerCase()
              }
              renderInput={(params) => (
                <TextField {...params} label="Filter by user" placeholder="All users" />
              )}
            />

            {/* Status */}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={filterStatus || "All"}
              onChange={(_, v) => setFilterStatus(v === "All" ? "" : v)}
            >
              <ToggleButton value="All">All</ToggleButton>
              <ToggleButton value="Active">Active</ToggleButton>
              <ToggleButton value="Inactive">Inactive</ToggleButton>
            </ToggleButtonGroup>

            {/* Category */}
            <Autocomplete
              multiple
              size="small"
              sx={{ width: 300 }}
              options={categoryOptionsScoped}
              value={filterCategories}
              onChange={(_, v) => setFilterCategories(v)}
              renderInput={(params) => (
                <TextField {...params} label="User Category" placeholder="Search user categories…" />
              )}
            />

            {selectedUser && (
              <Button size="small" onClick={() => setSelectedUser(null)}>Clear user</Button>
            )}
          </Box>
        )}

        {/* Existing table filters + Reviewed toggle */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          {/* Entity */}
          <TextField
            select size="small" label="Entity" value={entitySel}
            onChange={(e) => {
              const v = e.target.value;
              setEntitySel(Array.isArray(v) ? v : String(v).split(","));
              setProjPage(1);
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length ? `${selected.length} selected` : "All"),
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 220 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                autoFocus size="small" placeholder="Search entities…"
                value={entityQuery} onChange={(e) => setEntityQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()} fullWidth
              />
            </ListSubheader>
            <MenuItem
              value="__all__"
              onClick={() => setEntitySel(entitySel.length === entityOptions.length ? [] : entityOptions)}
            >
              <Checkbox
                size="small"
                checked={entitySel.length === entityOptions.length && entityOptions.length > 0}
                indeterminate={entitySel.length > 0 && entitySel.length < entityOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>
            {entityOptions
              .filter((v) => v.toLowerCase().includes(entityQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={entitySel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>

          {/* Project Name */}
          <TextField
            select size="small" label="Project Name" value={projectSel}
            onChange={(e) => {
              const v = e.target.value;
              setProjectSel(Array.isArray(v) ? v : String(v).split(","));
              setProjPage(1);
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length ? `${selected.length} selected` : "All"),
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 260 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                autoFocus size="small" placeholder="Search project…"
                value={projectQuery} onChange={(e) => setProjectQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()} fullWidth
              />
            </ListSubheader>

            <MenuItem
              value="__all__"
              onClick={() => setProjectSel(projectSel.length === projectOptions.length ? [] : projectOptions)}
            >
              <Checkbox
                size="small"
                checked={projectSel.length === projectOptions.length && projectOptions.length > 0}
                indeterminate={projectSel.length > 0 && projectSel.length < projectOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>

            {projectOptions
              .filter((v) => v.toLowerCase().includes(projectQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={projectSel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>

          {/* Category */}
          <TextField
            select size="small" label="Project Category" value={categorySel}
            onChange={(e) => {
              const v = e.target.value;
              setCategorySel(Array.isArray(v) ? v : String(v).split(","));
              setProjPage(1);
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length ? `${selected.length} selected` : "All"),
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 220 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                autoFocus size="small" placeholder="Search category…"
                value={categoryQuery} onChange={(e) => setCategoryQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()} fullWidth
              />
            </ListSubheader>
            <MenuItem
              value="__all__"
              onClick={() =>
                setCategorySel(categorySel.length === categoryOptions.length ? [] : categoryOptions)
              }
            >
              <Checkbox
                size="small"
                checked={categorySel.length === categoryOptions.length && categoryOptions.length > 0}
                indeterminate={categorySel.length > 0 && categorySel.length < categoryOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>
            {categoryOptions
              .filter((v) => v.toLowerCase().includes(categoryQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={categorySel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>

          {/* Due range */}
          <TextField
            label="Due from" type="date" size="small" value={dueFrom}
            onChange={(e) => { setDueFrom(e.target.value); setProjPage(1); }}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
          />
          <TextField
            label="Due to" type="date" size="small" value={dueTo}
            onChange={(e) => { setDueTo(e.target.value); setProjPage(1); }}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
          />

          {/* Stage */}
          <TextField
            select size="small" label="Stage" value={stageSel}
            onChange={(e) => {
              const v = e.target.value;
              setStageSel(Array.isArray(v) ? v : String(v).split(","));
              setProjPage(1);
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length ? `${selected.length} selected` : "All"),
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 200 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                size="small" placeholder="Search stage…"
                value={stageQuery} onChange={(e) => setStageQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()} fullWidth
              />
            </ListSubheader>
            <MenuItem
              value="__all__"
              onClick={() => setStageSel(stageSel.length === stageOptions.length ? [] : stageOptions)}
            >
              <Checkbox
                size="small"
                checked={stageSel.length === stageOptions.length && stageOptions.length > 0}
                indeterminate={stageSel.length > 0 && stageSel.length < stageOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>
            {stageOptions
              .filter((v) => v.toLowerCase().includes(stageQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={stageSel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>

          {/* Reviewed toggle filter */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Reviewed:
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={reviewedFilter}
              onChange={(_, v) => {
                if (v) {
                  setReviewedFilter(v);
                  setProjPage(1);
                }
              }}
            >
              <ToggleButton value="All">All</ToggleButton>
              <ToggleButton value="Reviewed">Reviewed</ToggleButton>
              <ToggleButton value="Not Reviewed">Not reviewed</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Button onClick={clearFilters} variant="outlined" size="small">Clear filters</Button>

          {/* Page size */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
            <Typography variant="body2">Show</Typography>
            <FormControl size="small">
              <Select
                value={projPageSize}
                onChange={(e) => { setProjPageSize(e.target.value); setProjPage(1); }}
              >
                {[5, 10, 20, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2">entries</Typography>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress />
          </Box>
        )}

        {/* Table */}
        <Table size="small" sx={{ "& th, & td": { py: 0.5, px: 1 }, "tbody td": { py: 0.5 } }}>
          <TableHead
            sx={{
              position: "sticky", top: 0, zIndex: 1,
              "& th": { bgcolor: (t) => t.palette.grey[100], color: (t) => t.palette.grey[900], fontWeight: 700 },
            }}
          >
            <TableRow>
              <TableCell align="center" sx={{ width: 40 }}>S. No.</TableCell>
              <TableCell onClick={() => handleSort("entity")} sx={{ cursor: "pointer" }}>
                ENTITY{arrow("entity")}
              </TableCell>
              <TableCell onClick={() => handleSort("title")} sx={{ cursor: "pointer" }}>
                PROJECT NAME{arrow("title")}
              </TableCell>
              <TableCell onClick={() => handleSort("dueDate")} sx={{ cursor: "pointer" }}>
                DUE DATE{arrow("dueDate")}
              </TableCell>
              <TableCell align="center" onClick={() => handleSort("deadline")} sx={{ cursor: "pointer" }}>
                PROJECT DEADLINE{arrow("deadline")}
              </TableCell>
              <TableCell align="center" onClick={() => handleSort("totalTasks")} sx={{ cursor: "pointer" }}>
                TOTAL{arrow("totalTasks")}
              </TableCell>
              <TableCell align="center" onClick={() => handleSort("pendingCount")} sx={{ cursor: "pointer" }}>
                PENDING{arrow("pendingCount")}
              </TableCell>
              <TableCell align="center" onClick={() => handleSort("inProgressCount")} sx={{ cursor: "pointer" }}>
                IN&nbsp;PROGRESS{arrow("inProgressCount")}
              </TableCell>
              <TableCell align="center" onClick={() => handleSort("completedCount")} sx={{ cursor: "pointer" }}>
                COMPLETED{arrow("completedCount")}
              </TableCell>

              {/* ✅ NEW: NA (after Completed, before Reviewed) */}
              <TableCell align="center" onClick={() => handleSort("naCount")} sx={{ cursor: "pointer" }}>
                NA{arrow("naCount")}
              </TableCell>

              {/* Reviewed (after NA, before Task Completion) */}
              <TableCell align="center" onClick={() => handleSort("reviewedCount")} sx={{ cursor: "pointer" }}>
                REVIEWED{arrow("reviewedCount")}
              </TableCell>

              <TableCell onClick={() => handleSort("completion")} sx={{ cursor: "pointer" }}>
                TASK COMPLETION{arrow("completion")}
              </TableCell>
              <TableCell onClick={() => handleSort("stage")} sx={{ cursor: "pointer" }}>
                TASK STAGE{arrow("stage")}
              </TableCell>
              <TableCell align="center">SUMMARY</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageRows.map((row, idx) => (
              <TableRow key={`${row.entity}-${row.title}-${idx}`}>
                <TableCell align="center">{fromIdx + idx + 1}</TableCell>
                <TableCell>{row.entity}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>{row.dueDate}</TableCell>
                <TableCell align="center">{row.deadline}</TableCell>
                <TableCell align="center">{row.totalTasks}</TableCell>
                <TableCell align="center">
                  {row.pendingCount > 0 ? (
                    <Chip size="small" color="error" label={row.pendingCount} />
                  ) : (
                    <Typography variant="body2">0</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {row.inProgressCount > 0 ? (
                    <Chip size="small" color="warning" label={row.inProgressCount} />
                  ) : (
                    <Typography variant="body2">0</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {row.completedCount > 0 ? (
                    <Chip size="small" color="success" label={row.completedCount} />
                  ) : (
                    <Typography variant="body2">0</Typography>
                  )}
                </TableCell>

                {/* ✅ NEW: NA count (neutral outlined) */}
                <TableCell align="center">
                  {row.naCount > 0 ? (
                    <Chip size="small" variant="outlined" label={row.naCount} />
                  ) : (
                    <Typography variant="body2">0</Typography>
                  )}
                </TableCell>

                {/* Reviewed count (neutral/outlined; not red like Pending) */}
                <TableCell align="center">
                  {row.reviewedCount > 0 ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="success"
                      label={row.reviewedCount}
                    />
                  ) : (
                    <Typography variant="body2">0</Typography>
                  )}
                </TableCell>

                <TableCell sx={{ minWidth: 200 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, Number(row.completion || 0)))}
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                    </Box>
                    <Typography variant="body2">
                      {Math.max(0, Math.min(100, Number(row.completion || 0)))}%
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip size="small" label={row.stage} color={chipColorForStageDMY(row.stage, row.dueDate)} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Open user & task summary">
                    <IconButton size="small" onClick={() => goToSummary(row)}>
                      <GridViewOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
          <Typography variant="body2">
            Showing <b>{sorted.length === 0 ? 0 : fromIdx + 1}</b> to <b>{toIdx}</b> of <b>{sorted.length}</b> entries
          </Typography>
          <Pagination
            page={projPage}
            count={Math.max(1, Math.ceil(sorted.length / projPageSize))}
            size="small"
            onChange={(_, p) => setProjPage(p)}
          />
        </Box>
      </Paper>
    </Box>
  );
}
